import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { login } from 'API/account';
import { getAvailableProducts, getFullLicense } from 'API/license';
import { getUserInfo } from 'API/user';
import { Products, Routes } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import { useExtensionAuth } from 'hooks/use-extension-auth';
import i18n from 'i18n';
import jwt from 'jsonwebtoken';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import { UserInfo } from 'types/user.types';
import isEmail from 'validator/lib/isEmail';

interface Props {
  open: boolean;
  handleClose: () => void;
}

const AdminReloginDialog: React.FC<Props> = ({ open, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { extensionSignIn } = useExtensionAuth();
  const signIn = useUserStore((state) => state.signIn);
  const signOut = useUserStore((state) => state.signOut);
  const setToken = useUserStore((state) => state.setToken);
  const setLicenses = useUserStore((state) => state.setLicenses);
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [helperTextUsername, setHelperTextUsername] = useState('');
  const [helperTextPassword, setHelperTextPassword] = useState('');

  const adminEmail = localStorage.getItem('adminEmail') || '';

  useEffect(() => {
    setHelperTextUsername('');
    setHelperTextPassword('');
  }, [password]);

  const isAllValid = () => password.trim() !== '' && isEmail(adminEmail);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleCloseAndReset = () => {
    setLoading(false);
    setPassword('');
    setHelperTextPassword('');
    setHelperTextUsername('');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminLoginTime');
    handleClose();
  };

  const handleSignIn = async () => {
    if (!isEmail(adminEmail)) {
      setHelperTextUsername('* ' + t('noValidEmailError'));
      return;
    }

    setLoading(true);

    const loginRes = await login({ email: adminEmail, password: password });

    if (loginRes.error) {
      if (loginRes.error.message?.includes('Incorrect email or password')) {
        setHelperTextPassword('* ' + t('makeSurePasswordCorrect'));
      } else {
        handleOpenErrorDialog(loginRes?.error, '/login', 'post');
      }
      setLoading(false);
      return;
    }

    if (loginRes.data) {
      // Store token immediately so subsequent API calls have auth
      setToken(loginRes.data.token);

      const [userInfoRes, productsRes, licensesRes] = await Promise.all([
        getUserInfo(),
        getAvailableProducts(),
        getFullLicense(),
      ]);

      const hasLicenseError = !Array.isArray(licensesRes) && 'error' in licensesRes;
      const licArr = Array.isArray(licensesRes) ? licensesRes : [];

      if (!userInfoRes.error && !productsRes.error && !hasLicenseError) {
        const userInfo: UserInfo = new UserInfo(userInfoRes);
        const products = productsRes.products;
        setLicenses(licArr);
        signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
      }

      if ((productsRes.error || hasLicenseError) && !userInfoRes.error) {
        const userInfo: UserInfo = new UserInfo(userInfoRes);
        const decoded: any = jwt.decode(loginRes.data.token.accessToken, { complete: true });
        if (decoded && decoded.payload && decoded.payload.possessedLicenses) {
          const products: Products[] = decoded.payload.possessedLicenses;
          signIn(loginRes.data.user, userInfo, products, loginRes.data.token);
        }
      }

      if (!hasLicenseError) {
        setLicenses(licArr);
      }

      if (userInfoRes.error) {
        handleOpenErrorDialog(userInfoRes?.error, '/users/me', 'get');
      }

      i18n.changeLanguage(loginRes.data.user.language);
      extensionSignIn(loginRes.data.token, loginRes.data.user);

      // After successful login
      handleCloseAndReset();
      navigate(Routes.AI_CHAT, { replace: true });
      window.location.reload();
    }

    setLoading(false);
  };

  const handleCancel = () => {
    // Ensure a proper logout
    signOut();
    localStorage.removeItem('rememberLinkedinAccount');
    handleCloseAndReset();
    navigate('/login');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isAllValid()) {
      handleSignIn();
    }
  };

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('adminReloginTitle')}</Typography>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <PaiTextField
            placeholder={t('emailShort')}
            value={adminEmail}
            onChange={() => {}}
            variant={'background'}
            error={helperTextUsername !== ''}
            errorText={helperTextUsername}
            disabled
          />
          <PaiTextField
            placeholder={t('password')}
            value={password}
            onChange={handlePasswordChange}
            type={'password'}
            error={helperTextPassword !== ''}
            errorText={helperTextPassword}
            onKeyDown={handleKeyDown}
          />
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('logout')} size={'md'} underline={'always'} action={handleCancel} />
        <PaiButton text={t('signIn')} border={'rounded'} size={'small'} action={handleSignIn} disabled={!isAllValid()} loading={loading} />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    position: 'relative',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  closeButton: {
    position: 'absolute',
    right: '16px',
    top: '12px',
    color: '#FFF',
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default AdminReloginDialog;
